import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  LessonsLearnedEntry,
  CreateLessonsLearnedEntryInput,
  UpdateLessonsLearnedEntryInput,
} from '../types/lessonsLearned';
import type { ApiResponse } from '../types/task';
import type { MilestoneTemplate, MilestoneTemplateItem } from './useMilestones';

const ENTRIES_KEY = 'lessons-learned-entries';

/**
 * List lessons-learned entries for a given year. If `year` is `null`/
 * `undefined`, the server picks the active year.
 */
export function useLessonsLearnedEntries(year?: number | null) {
  return useQuery({
    queryKey: [ENTRIES_KEY, year ?? null],
    queryFn: async () => {
      const path =
        year !== null && year !== undefined
          ? `/lessons-learned?year=${year}`
          : '/lessons-learned';
      const res = await api.get<{ data: LessonsLearnedEntry[] }>(path);
      return res.data;
    },
    // Wait until we actually know the year so we don't fire a
    // year=null request and end up with one cache entry per page mount.
    enabled: year !== null && year !== undefined,
    staleTime: 30 * 1000,
  });
}

export function useCreateLessonsLearnedEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateLessonsLearnedEntryInput) => {
      const res = await api.post<{ data: LessonsLearnedEntry }>(
        '/lessons-learned',
        input,
      );
      return res.data;
    },
    onSuccess: (entry) => {
      // Invalidate every (year-scoped) cache; cheap to refetch and keeps the
      // page consistent across year switchers.
      queryClient.invalidateQueries({ queryKey: [ENTRIES_KEY] });
      queryClient.setQueryData<LessonsLearnedEntry[] | undefined>(
        [ENTRIES_KEY, entry.year],
        (old) => {
          if (!old) return [entry];
          return [entry, ...old.filter((e) => e.id !== entry.id)];
        },
      );
    },
  });
}

export function useUpdateLessonsLearnedEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: UpdateLessonsLearnedEntryInput & { id: string }) => {
      const res = await api.patch<{ data: LessonsLearnedEntry }>(
        `/lessons-learned/${id}`,
        input,
      );
      return res.data;
    },
    onSuccess: (entry) => {
      queryClient.invalidateQueries({ queryKey: [ENTRIES_KEY] });
      queryClient.setQueryData<LessonsLearnedEntry[] | undefined>(
        [ENTRIES_KEY, entry.year],
        (old) => old?.map((e) => (e.id === entry.id ? entry : e)),
      );
    },
  });
}

export function useDeleteLessonsLearnedEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/lessons-learned/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ENTRIES_KEY] });
    },
  });
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Append a lesson to the canonical milestone template as a new item, then
 * mark the lesson resolved. The canonical template is the oldest one — same
 * pick the single-template settings page uses. Admin-only on the server
 * side; the UI also hides the button for non-admins.
 */
export function useConvertLessonToTemplateMilestone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entry,
      markResolved = true,
    }: {
      entry: LessonsLearnedEntry;
      markResolved?: boolean;
    }) => {
      const titleSource = entry.text.trim().replace(/\s+/g, ' ');
      const title =
        titleSource.length > 120 ? `${titleSource.slice(0, 117)}…` : titleSource;

      const templatesRes = await api.get<ApiResponse<MilestoneTemplate[]>>(
        '/milestones/templates',
      );
      const templates = templatesRes.data ?? [];
      if (templates.length === 0) {
        throw new Error('No milestone template exists yet.');
      }
      const template = [...templates].sort((a, b) =>
        a.createdAt.localeCompare(b.createdAt),
      )[0];

      // Find a free key — slugified title, with -2/-3/… suffixes on collision.
      const existingKeys = new Set(template.items.map((i) => i.key));
      const base = slugify(title) || `lesson-${entry.id.slice(0, 8)}`;
      let key = base;
      let n = 2;
      while (existingKeys.has(key)) {
        key = `${base}-${n}`;
        n++;
      }

      // Anchor the new item to the existing root so the timeline stays
      // sensible. If there's no clear root, fall back to the first item's key.
      const root = template.items.find((i) => i.anchor)
        ?? template.items.find((i) => !i.anchorKey && i.fixedDate === undefined)
        ?? template.items[0];

      const newItem: MilestoneTemplateItem = {
        key,
        title,
        description: titleSource.length > 120 ? entry.text.trim() : undefined,
        domain: entry.domain,
        domains: entry.domain ? [entry.domain] : undefined,
        daysFromAnchor: 0,
        anchorKey: root ? root.key : undefined,
      };

      const nextItems = [...template.items, newItem];
      await api.put<ApiResponse<MilestoneTemplate>>(
        `/milestones/templates/${encodeURIComponent(template.id)}`,
        {
          name: template.name,
          description: template.description,
          items: nextItems,
          version: template.version,
        },
      );

      if (markResolved && entry.status !== 'resolved') {
        await api.patch<{ data: LessonsLearnedEntry }>(
          `/lessons-learned/${entry.id}`,
          {
            status: 'resolved',
            version: entry.version,
          },
        );
      }

      return { templateId: template.id, key, entry };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ENTRIES_KEY] });
      queryClient.invalidateQueries({ queryKey: ['milestone-templates'] });
    },
  });
}
